import * as React from 'react';
import { Alert, Button, Card, Modal, Spinner } from 'react-bootstrap';
import { PencilSquare } from 'react-bootstrap-icons';
import { useFetcher, useLoaderData, useLocation } from 'react-router-dom';

import { CustomPay } from '@/components/custompay';
import { FormButtons } from '@/components/formbuttons';
import { InfoTable } from '@/components/infotable';
import { Input } from '@/components/input';
import { InputNumberDummy } from '@/components/input-number-dummy';
import { InputPhone } from '@/components/input-phone';
import { ModalForm } from '@/components/modal-form';
import { Patcher } from '@/components/patcher';
import { PatcherAPISelector } from '@/components/patcher-api-selector';
import { PatcherInputNumber } from '@/components/patcher-input-number';
import { PatcherSwitch } from '@/components/patcher-switch';
import { SelectorList } from '@/components/selector-list';
import { deleteSingle, getSingle, patchSingle, postSingle, putSingle } from '@/helper/apicalls';
import { User } from '@/helper/interfaces';
import { allCountries, allStates } from '@/helper/lists';
import { formatAddress, formatPhone } from '@/helper/utility';
import { useStore } from '@/store';

import { baseURL } from './const';

export async function singleLoader({ params }: any) {
  if (Number.isInteger(Number.parseInt(params.id, 10))) {
    return getSingle(baseURL, params.id);
  } else {
    throw new TypeError('404! This is not a valid URL.');
  }
}

export async function singleAction({ request, params }: any) {
  const body = Object.fromEntries(await request.formData());
  const redirectTo = body.redirectTo;
  delete body.redirectTo;

  if (body.roleId) body.roleId = JSON.parse(body.roleId);

  if (body.yearlyPta) body.yearlyPta = JSON.parse(body.yearlyPta);

  if (request.method == 'POST') {
    return postSingle(baseURL, body);
  } else if (request.method == 'PUT') {
    return putSingle(baseURL, params.id, body);
  } else if (request.method == 'PATCH') {
    return patchSingle(baseURL, params.id, body);
  } else if (request.method == 'DELETE') {
    return deleteSingle(baseURL, params.id, redirectTo);
  } else {
    return;
  }
}

export function Single() {
  const data = useLoaderData() as User;

  const { user } = useStore();

  const location = useLocation();
  const [redirectTo] = React.useState(location.state?.from || baseURL);

  const isSuperAdmin = data.id === 1 ? true : false;

  const modal = React.useRef(null);
  const passwordResetFetcher = useFetcher();
  const [modalPasswordReset, setModalPasswordReset] = React.useState(false);
  const [passwordResetSubmitted, setPasswordResetSubmitted] = React.useState(false);
  const passwordResetLoading = passwordResetFetcher.state == 'loading' || passwordResetFetcher.state == 'submitting';
  const passwordResetResponse =
    passwordResetSubmitted && !passwordResetLoading ? (passwordResetFetcher.data as any) : undefined;
  const passwordResetSucceeded = passwordResetResponse?.status == 200;
  const passwordResetError =
    passwordResetResponse && !passwordResetSucceeded
      ? typeof passwordResetResponse === 'string'
        ? passwordResetResponse
        : passwordResetResponse?.message || 'There was an error sending the password link.'
      : '';

  function closePasswordResetModal() {
    if (!passwordResetLoading) {
      setModalPasswordReset(false);
      setPasswordResetSubmitted(false);
    }
  }

  function openPasswordResetModal() {
    setPasswordResetSubmitted(false);
    setModalPasswordReset(true);
  }

  function sendPasswordReset() {
    setPasswordResetSubmitted(true);
    // eslint-disable-next-line unicorn/no-null
    passwordResetFetcher.submit(null, {
      method: 'post',
      action: '/users/' + data.id + '/password-reset'
    });
  }

  return (
    <React.Fragment>
      {data.deleted && (
        <Alert variant='danger' className='mt-3'>
          This user was deleted and can no longer be edited.
        </Alert>
      )}
      {isSuperAdmin && (
        <Alert variant='warning' className='mt-3'>
          Super Admin cannot be edited.
        </Alert>
      )}

      <Card>
        <Card.Body>
          <Card.Title className='clearfix'>
            User Info
            <Button
              type='button'
              size='sm'
              className='float-end'
              onClick={() => modal.current.openModal()}
              disabled={data.deleted || isSuperAdmin}
            >
              <PencilSquare className='icon-align' /> Edit
            </Button>
          </Card.Title>
          <InfoTable
            elements={[
              { title: 'Name', content: data.name },
              { title: 'Email', content: data.email },
              ...(isSuperAdmin
                ? []
                : [
                    { title: 'Phone', content: formatPhone(data.phone) },
                    {
                      title: 'Address',
                      content: formatAddress(data.address, data.city, data.state, data.zip, data.country)
                    }
                  ])
            ]}
          />
        </Card.Body>
      </Card>

      {!user?.role?.editPermissions && (
        <Alert variant='warning' className='mt-3'>
          You are not authorized to edit user permissions.
        </Alert>
      )}

      <Card className='mt-3'>
        <Card.Body>
          <Card.Title className='clearfix'>User Options</Card.Title>
          <Patcher
            id={data.id}
            values={{
              isActive: data.isActive.toString(),
              isMixer: data.isMixer.toString(),
              isAssistant: data.isAssistant.toString(),
              roleId: data.roleId.toString(),
              yearlyPta: data.yearlyPta
            }}
            url='/users'
            disabled={data.deleted}
          >
            <PatcherSwitch
              name='isActive'
              displayName='Active'
              inactive={isSuperAdmin || !user?.role?.editPermissions}
            />
            <PatcherSwitch name='isMixer' displayName='Mixer' inactive={isSuperAdmin || !user?.role?.editPermissions} />
            <PatcherSwitch
              name='isAssistant'
              displayName='Assistant'
              inactive={isSuperAdmin || !user?.role?.editPermissions}
            />
            <PatcherAPISelector
              name='role'
              fetchURL='/users/roles'
              size='col-3'
              inactive={isSuperAdmin || !user?.role?.editPermissions}
            />
            <PatcherInputNumber
              name='yearlyPta'
              displayName='Yearly PTA'
              size='col-3'
              days
              inactive={isSuperAdmin || !user?.role?.editPermissions}
            />
          </Patcher>
          <InputNumberDummy
            name='rolloverPta'
            displayName='Rollover PTA'
            size='col-3'
            days
            value={data.rolloverPta.toString()}
          />
          <div className='mt-3'>
            <Button
              variant='primary'
              type='button'
              onClick={openPasswordResetModal}
              disabled={data.deleted || isSuperAdmin || !user?.role?.editPermissions}
            >
              Send Password Reset Email
            </Button>
          </div>
        </Card.Body>
      </Card>

      {data.isMixer && (
        <CustomPay
          id={data.id}
          value={{
            services: data.customRates,
            rate: data.rate,
            usePercentage: data.usePercentage,
            percentage: data.percentage
          }}
          baseURL={baseURL}
          disabled={data.deleted}
        />
      )}

      <FormButtons type='User' redirectTo={redirectTo} showDelete={!data.deleted && !isSuperAdmin}>
        <React.Fragment>Are you sure you want to delete {data.name}?</React.Fragment>
      </FormButtons>

      <ModalForm title='Edit Info' size='lg' url={baseURL} putId={data.id} ref={modal} draggable>
        <Input name='firstName' size='col-6' value={data.firstName} />
        <Input name='lastName' size='col-6' value={data.lastName} />
        <Input name='email' size='col-6' value={data.email} />
        <InputPhone size='col-6' value={data.phone} />
        <Input name='address' value={data.address} />
        <Input name='city' size='col-6' value={data.city} />
        <SelectorList name='state' list={allStates} size='col-4' value={data.state} />
        <Input name='zip' size='col-2' value={data.zip} />
        <SelectorList name='country' list={allCountries} size='col-12' value={data.country} />
      </ModalForm>

      <Modal show={modalPasswordReset} onHide={closePasswordResetModal}>
        <Modal.Header closeButton>
          <Modal.Title>Send Password Link?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {passwordResetError && <Alert variant='danger'>{passwordResetError}</Alert>}
          {passwordResetSucceeded ? (
            <Alert variant='success'>{passwordResetResponse.data.message}</Alert>
          ) : (
            <React.Fragment>
              This will email {data.name} a secure link to set a new password. Any older unused password links for this
              user will stop working.
            </React.Fragment>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='secondary'
            type='button'
            className='me-2'
            onClick={closePasswordResetModal}
            disabled={passwordResetLoading}
          >
            {passwordResetSucceeded ? 'Close' : 'Cancel'}
          </Button>
          {!passwordResetSucceeded && (
            <Button variant='primary' type='button' onClick={sendPasswordReset} disabled={passwordResetLoading}>
              {passwordResetLoading ? (
                <React.Fragment>
                  <Spinner as='span' size='sm' role='status' aria-hidden='true' />
                  &nbsp;Sending...
                </React.Fragment>
              ) : (
                'Send Email'
              )}
            </Button>
          )}
        </Modal.Footer>
      </Modal>
    </React.Fragment>
  );
}
